// THE CONDITION AND COMPS STEPS' WORDS ARE THE APP'S (WEB-PARITY-3). Tyler, 2026-09-29: "the site should replicate exactly
// what the app has at this time".
//
// Every label, caption, notice and alert on `/condition` and `/comps` that the server does not send is the app's own
// string, copied unchanged from the line it cites (under `AppealAce/`), as `detailsWording.ts` is for step 1. A `{name}`
// is the app's own interpolation written the web's way (`\(seen)` → `{seen}`); where the app chooses between two words
// inside an interpolation (`\(seen == 1 ? "sale" : "sales")`), each word is its own entry, on the same line. They carry no
// new approval: the approval is Tyler's standing instruction (`WEB_PARITY`), and `test/parity3.test.tsx` reads each cited
// line and fails when the app's words and these differ.
//
// NOT HERE, ON PURPOSE:
//   - the non-disclosure note and its covered list (CompsView.swift:140-188): no state the server serves is a
//     non-disclosure state, and which states are is a list the app holds and the server does not (F-WP3-6);
//   - the non-premium footers and the credit prompts (CaseEvidenceCards.swift:113, :258; CompsView.swift:305, :353-375):
//     every page here is behind the spend, where the app shows the premium words;
//   - "Take a photo" where the device has no camera (`CameraPicker.isAvailable`, CaseEvidenceCards.swift:135): the page
//     offers it only on a touch device;
//   - the unit-count field and its help (CompEditView.swift:184-189): the app shows them only for income property, and
//     income is dark on both clients (`isIncomeProperty`, SuggestedValue.swift:1481-1485; v2's NEW-20).

import { WEB_PARITY, type AppWording } from "../../components/heldCopy";

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

const COND = "Features/Reasons/PropertyConditionView.swift";
const CEC = "Features/Reasons/CaseEvidenceCards.swift";
const DIS = "Shared/Disclaimers.swift";
const CV = "Features/Comps/CompsView.swift";
const CE = "Features/Comps/CompEditView.swift";
const AV = "Features/Reasons/AnalyzeView.swift";

export const PARITY3_WORDING = {
  // ══ /condition — PropertyConditionView and CaseEvidenceCards (CD-1 to CD-9) ══
  "condition.hero.title": app(`${COND}:25`, "Anything we should know?"),
  "condition.hero.unit": app(
    `${COND}:29`,
    "Note condition or damage that lowers your unit's value below comparable units — dated kitchens and baths, aging systems inside the unit, water damage, a pending special assessment. Add photos and we'll describe what each one shows the board.",
  ),
  "condition.hero.house": app(
    `${COND}:30`,
    "Note condition or damage that lowers your home's value below typical comps — a worn roof, foundation cracks, water damage, dated systems. Add photos and we'll describe what each one shows the board.",
  ),
  "condition.card.title": app(`${CEC}:50`, "Condition & damage"),
  "condition.card.footer": app(
    `${CEC}:52`,
    "The assessor priced your home as if it were in normal shape — this is where you tell them otherwise, so be thorough. For each problem, spell out what's wrong, how bad it is, and why it makes the home worth less than the assessment assumes. Repair quotes, dates, and specifics make it far more persuasive. Stick to facts about the property — not your income or ability to pay.",
  ),
  "condition.chip.unit.kitchen": app(`${CEC}:30`, "Outdated kitchen/bath"),
  "condition.chip.unit.hvac": app(`${CEC}:30`, "Aging HVAC"),
  "condition.chip.unit.electrical": app(`${CEC}:30`, "Old electrical/plumbing"),
  "condition.chip.unit.water": app(`${CEC}:31`, "Water damage inside the unit"),
  "condition.chip.unit.special": app(`${CEC}:31`, "Special assessment pending"),
  "condition.chip.unit.hoa": app(`${CEC}:31`, "High monthly HOA fee"),
  "condition.chip.unit.renovation": app(`${CEC}:32`, "Needs full renovation"),
  "condition.chip.house.roof": app(`${CEC}:33`, "Roof at end of life"),
  "condition.chip.house.foundation": app(`${CEC}:33`, "Foundation cracks"),
  "condition.chip.house.water": app(`${CEC}:33`, "Water/flood damage"),
  "condition.chip.house.mold": app(`${CEC}:33`, "Mold"),
  "condition.chip.house.kitchen": app(`${CEC}:34`, "Outdated kitchen/bath"),
  "condition.chip.house.hvac": app(`${CEC}:34`, "Aging HVAC"),
  "condition.chip.house.electrical": app(`${CEC}:34`, "Old electrical/plumbing"),
  "condition.chip.house.renovation": app(`${CEC}:34`, "Needs full renovation"),
  "condition.placeholder.unit": app(
    `${CEC}:43`,
    "e.g. \"The kitchen and both baths are untouched since the 1990s — laminate counters, original cabinets, a tub that needs replacing. The HVAC air handler is original to the unit and a contractor quoted $9,200 to replace it. The association has also approved a special assessment of $14,000 per unit for the facade, payable next year. A buyer would knock all of this off their offer, but the assessment treats the unit as updated.\"",
  ),
  "condition.placeholder.house": app(
    `${CEC}:44`,
    "e.g. \"Roof is original 2002 architectural shingle, curling and missing tabs on the south slope; a roofer quoted $14,500 to replace it. The kitchen and both baths are untouched since the 1990s — laminate counters, original cabinets. A buyer would knock all of this off their offer, but the assessment treats the home as updated.\"",
  ),
  "photos.title": app(`${CEC}:105`, "Photo evidence"),
  "photos.footer": app(`${CEC}:112`, "Caption each photo describing what it shows and how it affects value. {exhibit}"),
  "photos.exhibit": app(`${DIS}:38`, "What you write here is printed under the photo on the exhibit your board receives."),
  "photos.remove": app(`${CEC}:125`, "Remove"),
  "photos.take": app(`${CEC}:139`, "Take a photo"),
  "photos.library": app(`${CEC}:144`, "Add from library"),
  "photos.addDescription": app(`${CEC}:198`, "Add description"),
  "photos.sheet.title": app(`${CEC}:264`, "Photo"),
  "photos.sheet.placeholder": app(`${CEC}:225`, "Tell the board what this shows and why it lowers your home's value"),
  "photos.sheet.draft": app(`${CEC}:241`, "Draft with AI"),
  "photos.sheet.drafting": app(`${CEC}:241`, "Drafting…"),
  "photos.sheet.header": app(`${CEC}:249`, "What is this telling the board?"),
  "photos.sheet.footer": app(
    `${CEC}:256`,
    "Write what the issue is and how it hurts value — or tap Draft with AI and we'll look at the photo and write a board-ready description you can edit. {exhibit}",
  ),
  "photos.sheet.done": app(`${CEC}:269`, "Done"),
  "photos.sheet.notUseful": app(
    `${CEC}:300`,
    "This photo probably won't affect your assessment. Boards focus on damage, water, foundation, roof, or dated systems.",
  ),
  "photos.sheet.draftFailed": app(`${CEC}:305`, "Couldn't draft a caption right now. Try again."),
  "condition.footnote": app(`${COND}:51`, "Optional — but condition evidence strengthens a thin comp case."),
  "condition.continue": app(`${COND}:55`, "Continue to comps"),

  // ══ /comps — CompsView (CP-1 to CP-15) ══
  "comps.boardInfo.banner": app(`${CV}:65`, "Your appeal is judged as of {date} — sales are compared to that date, not today."),
  "comps.boardInfo.link": app(`${CV}:71`, "Will the board accept these?"),
  "comps.thinMarket": app(
    `${CV}:87`,
    "Your area has few recent sales, so the closest comparable homes sold longer ago — that's normal in lower-volume markets, and boards expect it. What matters is how comparable each home is and that we've adjusted its sale price to your valuation date.",
  ),
  "comps.continue.analyze": app(`${CV}:124`, "Analyze my comps"),
  "comps.continue.withoutComps": app(`${CV}:125`, "Continue without comps"),
  "comps.continue.addFirst": app(`${CV}:126`, "Add a comp to continue"),
  "comps.hero.title": app(`${CV}:193`, "Comparable sales"),
  "comps.hero.body": app(
    `${CV}:194`,
    "Recent sales of similar nearby homes are the backbone of your appeal. We rank candidates by how closely they match yours — size, beds/baths, age, and distance.",
  ),
  "comps.helper.addFirst": app(`${CV}:223`, "Add at least one comparable sale — your comps are what the appeal is built on."),
  "comps.helper.searchedEmpty": app(
    `${CV}:235`,
    "The automatic search didn’t find sales with usable prices nearby — often a data-coverage gap, not a sign your home has no comps. Add a recent sale you know of below; your credit already covers the full appeal (letter and evidence packet included).",
  ),
  "comps.matchInfo.title": app(`${CV}:284`, "How match is scored"),
  "comps.gotIt": app(`${CV}:285`, "Got it"),
  "comps.matchInfo.message": app(
    `${CV}:287`,
    "Match is how closely a sale resembles your home — on size, distance, beds/baths, how recent the sale is, and age. It's a similarity guide to help you read the list, not a value or an appraisal. Your requested value comes from the size- and time-adjusted comparison on the next steps.",
  ),
  "comps.boardInfo.title": app(`${CV}:289`, "Will the board accept these?"),
  "comps.boardInfo.message": app(
    `${CV}:292`,
    "Your appeal is judged as of your valuation date, not today. Sales within about a year of that date are standard and routinely considered. Older sales are still used — with a market-conditions (time) adjustment and a short explanation, which we handle — and in areas with few recent sales, boards expect older comps. Sales far from the valuation date (roughly 18+ months) carry less weight. This sets expectations, not a guarantee — PROppeal is a self-help tool, not an appraisal.",
  ),
  "comps.search.title": app(`${CV}:301`, "Find comps"),
  "comps.search.footer": app(`${CV}:304`, "Auto-search runs once per appeal. Add or remove comps yourself anytime."),
  "comps.search.progress.1": app(`${CV}:313`, "Searching nearby sales…"),
  "comps.search.progress.2": app(`${CV}:314`, "Pulling recent comparables…"),
  "comps.search.progress.3": app(`${CV}:315`, "Ranking by closeness to your home…"),
  "comps.search.progress.4": app(`${CV}:316`, "Filtering out distant and distressed sales…"),
  "comps.search.button": app(`${CV}:324`, "Auto-search comparable sales"),
  "comps.search.complete": app(`${CV}:339`, "Auto-search complete"),
  "comps.search.completeBody": app(
    `${CV}:349`,
    "Your licensed sale records were pulled once for this appeal. Searching again would return the same sales, so this list below is where you shape the set — remove one, or add a sale you know of.",
  ),
  "comps.reference.oneNumber": app(`${CV}:404`, "We found {shown} recent nearby {sales} that matched your home — ranked below."),
  "comps.reference.sale": app(`${CV}:402`, "sale"),
  "comps.reference.sales": app(`${CV}:402`, "sales"),
  "comps.reference.seen": app(
    `${CV}:406`,
    "We looked at {seen} recent nearby sales and ranked the {shown} closest matches to your home below.",
  ),
  "comps.header": app(`${CV}:437`, "YOUR COMPS"),
  "comps.header.usable": app(`${CV}:442`, "{n} usable"),
  "comps.empty.title": app(`${CV}:520`, "No comps yet"),
  "comps.empty.body": app(
    `${CV}:522`,
    "Add recent sales of similar nearby homes — the heart of your appeal. Auto-search above, or add them yourself.",
  ),
  "comps.empty.creditApplied": app(
    `${CV}:531`,
    "Your credit is already applied — adding a sale you know of keeps your full appeal going, including the drafted letter and evidence packet.",
  ),
  "comps.remove": app(`${CV}:568`, "Remove"),
  "comps.resources.title": app(`${CV}:597`, "Where to find recent sales"),
  "comps.resources.lead": app(
    `${CV}:603`,
    "Your county publishes sale prices — a recorded sale is what a review board trusts most. Good places to look:",
  ),
  "comps.resources.county": app(
    `${CV}:613`,
    "Your county recorder or assessor — search “{county} County property records” for recent sales near your address; this is the official record of what homes sold for.",
  ),
  "comps.resources.countyUnnamed": app(
    `${CV}:614`,
    "Your county recorder or assessor’s website — search recent sales near your address; this is the official record of what homes sold for.",
  ),
  "comps.resources.listing": app(
    `${CV}:615`,
    "Zillow or Redfin — filter to “Recently sold” to spot nearby sales fast, then confirm the price against county records.",
  ),
  "comps.resources.agent": app(`${CV}:616`, "A local real estate agent — can pull recent nearby sales from the MLS, often as a courtesy."),
  "comps.resources.footer": app(
    `${CV}:620`,
    "Enter the address in “Add a comp” and we’ll try to fill in the rest. Recent, arm’s-length sales closest in size and location help most.",
  ),
  "comps.add": app(`${CV}:653`, "Add a comp yourself"),
  "comps.result.typeFiltered": app(
    `${CV}:753`,
    "We found {seen} nearby {saleWord}, but {notType} {kind} property — a single-family sale isn't a comparable for your building, so we left {it} out rather than put {it2} in front of a board.",
  ),
  "comps.result.typeFiltered.sale": app(`${CV}:753`, "sale"),
  "comps.result.typeFiltered.sales": app(`${CV}:753`, "sales"),
  "comps.result.typeFiltered.itWasnt": app(`${CV}:753`, "it wasn't"),
  "comps.result.typeFiltered.nWerent": app(`${CV}:753`, "{n} of them weren't"),
  "comps.result.typeFiltered.it": app(`${CV}:753`, "it"),
  "comps.result.typeFiltered.them": app(`${CV}:753`, "them"),
  "comps.result.typeFiltered.sameType": app(`${CV}:752`, "same-type"),
  "comps.result.typeFiltered.addSimilar": app(
    `${CV}:757`,
    " Add a sale of a similar property you know of below to keep going; your credit already covers the full appeal.",
  ),
  "comps.result.noPrice": app(
    `${CV}:761`,
    "We found {seen} nearby {homeWord}, but none had a recorded sale price we could use — common where sales aren’t in the automated data, not a sign your home has no comps. Add a recent sale you know of below to keep going; your credit already covers the full appeal.",
  ),
  "comps.result.noPrice.home": app(`${CV}:761`, "home"),
  "comps.result.noPrice.homes": app(`${CV}:761`, "homes"),
  "comps.result.none": app(
    `${CV}:763`,
    "No recent nearby sales came back for this address. Add a sale you know of below to keep going — your credit already covers the full appeal.",
  ),
  "comps.result.added": app(`${CV}:767`, "Added {n} ranked {matchWord}."),
  "comps.result.added.match": app(`${CV}:767`, "match"),
  "comps.result.added.matches": app(`${CV}:767`, "matches"),

  // ── the comp card (CompsView.swift:842-1172) ──
  "card.noAddress": app(`${CV}:896`, "(no address)"),
  "card.ownerProvided": app(`${CV}:911`, "Owner-provided comparable sale"),
  "card.match": app(`${CV}:922`, "Match"),
  "card.matchLabel": app(`${CV}:949`, "Match {score} out of 100"),
  "card.beds": app(`${CV}:961`, "{beds} bd"),
  "card.baths": app(`${CV}:964`, "{baths} ba"),
  "card.distance": app(`${CV}:967`, "{miles} mi"),
  "card.timeAdjusted": app(`${CV}:985`, "Time-adjusted to {price} · market {sign}{pct}% since sale"),
  "card.timeGap": app(`${CV}:994`, "Not time-adjusted — using raw sale price (missing sale date or market data for this sale)"),
  "card.size.trimmed": app(`${CV}:1027`, "larger than yours, so we trimmed its price to match your size"),
  "card.size.raised": app(`${CV}:1028`, "smaller than yours, so we raised its price to match your size"),
  "card.size.same": app(`${CV}:1029`, "about the same size as yours"),
  "card.size.line": app(`${CV}:1030`, "Adjusted to your size: {adjusted} — this sale is {note}"),
  "card.size.notSizedTimeAdjusted": app(
    `${CV}:1039`,
    "{adjusted} adjusted to the valuation date. Not adjusted for size — your home's square footage isn't on file, so this sale is shown at its own size.",
  ),
  "card.size.notSizedAsSold": app(
    `${CV}:1040`,
    "{adjusted} as sold. Not adjusted for size — your home's square footage isn't on file, so this sale is shown at its own size.",
  ),
  "card.size.large": app(`${CV}:1048`, "Large {pct} size adjustment — a board may give this comp less weight."),
  "card.aboveImplied": app(
    `${CV}:1054`,
    "Adjusts to or above your implied value — kept here for transparency, but it doesn't support a reduction on its own.",
  ),
  "card.needsPrice": app(`${CV}:1065`, "Add a sale price — this comp won't count until you do"),
  "card.excluded": app(`${CV}:1074`, "Excluded — won't appear in the letter"),
  "card.sold.at": app(`${CV}:1111`, "at your {date} valuation date"),
  "card.sold.month": app(`${CV}:1112`, "month"),
  "card.sold.months": app(`${CV}:1112`, "months"),
  "card.sold.before": app(`${CV}:1116`, "{months} {unit} before your {date} valuation date"),
  "card.sold.after": app(
    `${CV}:1117`,
    "{months} {unit} after your {date} valuation date — usable, though some boards prefer sales on or before that date",
  ),
  "card.sold": app(`${CV}:1127`, "Sold {relation}."),
  "card.recency.recent": app(`${CV}:1136`, "Recent"),
  "card.recency.standard": app(`${CV}:1137`, "Standard — boards routinely accept"),
  "card.recency.older": app(`${CV}:1138`, "Older — accepted with the time adjustment"),
  "card.recency.dated": app(`${CV}:1139`, "Dated — a board may weigh this less"),
  "card.sqft": app(`${CV}:1153`, "{sqft} sqft"),

  // ── the add/edit sheet (CompEditView.swift) ──
  "sheet.warn.aboveBasis": app(
    `${CE}:123`,
    "This sale ({price}) is above your home's supported value ({basis}) — it likely won't help your appeal. You can still add it.",
  ),
  "sheet.warn.stale": app(`${CE}:128`, "This sale is over two years old — older sales carry less weight at the board."),
  "sheet.warn.distance": app(`${CE}:132`, "This home is {miles} mi away — more distant comps carry less weight."),
  "sheet.address": app(`${CE}:142`, "Address"),
  "sheet.salePrice": app(`${CE}:143`, "Sale price"),
  "sheet.dollar": app(`${CE}:143`, "$"),
  "sheet.hasSaleDate": app(`${CE}:144`, "Has sale date"),
  "sheet.saleDate": app(`${CE}:146`, "Sale date"),
  "sheet.property": app(`${CE}:154`, "Comparable property"),
  "sheet.propertyFooter": app(
    `${CE}:159`,
    "Enter the actual closed sale price you found — a county record or a listing site's sold history, not an automated estimate.",
  ),
  "sheet.lookingUp": app(`${CE}:170`, "Looking up…"),
  "sheet.lookUp": app(`${CE}:170`, "Look up size, beds & baths"),
  "sheet.sqft": app(`${CE}:179`, "Square footage"),
  "sheet.sqftUnit": app(`${CE}:179`, "sq ft"),
  "sheet.beds": app(`${CE}:191`, "Bedrooms"),
  "sheet.baths": app(`${CE}:192`, "Bathrooms"),
  "sheet.distance": app(`${CE}:193`, "Distance"),
  "sheet.miUnit": app(`${CE}:193`, "mi"),
  "sheet.characteristics": app(`${CE}:195`, "Characteristics"),
  "sheet.include": app(`${CE}:211`, "Include in appeal"),
  "sheet.includeFooter": app(`${CE}:213`, "Excluded comps stay saved but won't appear in the letter or PDF."),
  "sheet.title.add": app(`${CE}:218`, "Add Comp"),
  "sheet.title.edit": app(`${CE}:218`, "Edit Comp"),
  "sheet.cancel": app(`${CE}:224`, "Cancel"),
  "sheet.save": app(`${CE}:227`, "Save"),

  // ══ /analyze — the app's step 4 hero, the step the comps step's Continue leads to (AN-1, AN-3) ══
  "analyze.hero.title": app(`${AV}:145`, "Analyze your comps"),
  "analyze.hero.body": app(
    `${AV}:146`,
    "Our Evidence Engine compares every nearby sale to your home and keeps the ones that genuinely point to a lower value — chosen for how well they match your home, not just for being cheap.",
  ),
} as const satisfies Record<string, AppWording>;

export type Parity3WordingId = keyof typeof PARITY3_WORDING;

export function say(id: Parity3WordingId): string {
  return PARITY3_WORDING[id].text;
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function sayWith(id: Parity3WordingId, values: Readonly<Record<string, string | number>>): string {
  return say(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`sayWith(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
